import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { catchError, concatMap, defaultIfEmpty, defer, EMPTY, forkJoin, from, ignoreElements, map, mergeMap, Observable, of, tap, throwError, toArray } from 'rxjs';
import { DataTypes, IConfiguration, IInfoColumn } from '../../../production-plan/models/production-plan.models';
import { ProductionPlanConfigurationService } from '../../../production-plan/production-plan-configuration.service';
import { productionPlanValidators, validateProductionPlanField } from '../../../production-plan/production-plan.validators';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { JsonFileTransfer, jsonImportError } from '../file-transfer-services/jsonFileTransfer.service';
import { ITerminal, TerminalAdministrationService } from '../terminalAdministration.service';
import { FailedImport, FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const fileName = 'ProductionPlans';

export interface IInfoColumnFile {
	title: string;
	source: string;
	type: string;
	format: string;
}

/** The terminals and plans a status column refers to are named by alias, not by the environment-specific ids the plan stores. */
export interface IStatusColumnFile {
	title: string;
	terminals: string[];
	planLinks: string[];
}

export interface IProductionPlanFile {
	alias: string;
	description: string;
	orderPositionSql: string;
	infoColumns: IInfoColumnFile[];
	statusColumns: IStatusColumnFile[];
}

const InfoColumnSchema: IFileDataSchema<IInfoColumnFile> = {
	name: 'InfoColumn',
	properties: {
		title: { type: 'string', required: true },
		source: { type: 'string', required: true },
		type: { type: 'string', required: true },
		format: { type: 'string', required: false },
	},
};

const StatusColumnSchema: IFileDataSchema<IStatusColumnFile> = {
	name: 'StatusColumn',
	properties: {
		title: { type: 'string', required: true },
		terminals: { type: { array: 'string' }, required: false },
		planLinks: { type: { array: 'string' }, required: false },
	},
};

const ProductionPlanSchema: IFileDataSchema<IProductionPlanFile> = {
	name: fileName,
	properties: {
		alias: { type: 'string', required: true },
		description: { type: 'string', required: false },
		orderPositionSql: { type: 'string', required: false },
		infoColumns: { type: { array: { schema: InfoColumnSchema } }, required: true },
		statusColumns: { type: { array: { schema: StatusColumnSchema } }, required: true },
	},
};

interface IFileLink {
	alias: string;
	location: string;
}

interface IFileStatusColumn {
	title: string;
	terminals: IFileLink[];
	planLinks: IFileLink[];
}

interface IFilePlan {
	alias: string;
	description: string;
	orderPositionSql: string;
	location: string;
	infoColumns: IInfoColumn[];
	statusColumns: IFileStatusColumn[];
}

/** A plan is one record in the file with its columns nested inside it; a status column names its terminals and linked plans by alias instead of the environment-specific ids the plan stores. */
@Injectable({
	providedIn: 'root',
})
export class ProductionPlanTransferService implements ITransferService<IConfiguration> {
	private translationPrefix = 'mes.production-plan.admin';

	constructor(
		private configurationService: ProductionPlanConfigurationService,
		private terminalService: TerminalAdministrationService,
		private jsonFileTransfer: JsonFileTransfer<IProductionPlanFile>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('The production plans have no import template');
	};

	public exportData(configurations: IConfiguration[]): Observable<IConfiguration[]> {
		// Both rosters are read in full: a name only identifies a terminal or plan if no other one carries it.
		return forkJoin({
			terminals: this.terminalService.getTerminals({ includeArriveLeaveTerminals: true }),
			plans: this.configurationService.get(),
		}).pipe(
			catchError((error) => {
				// What the server answered is for the console: the user is told only that the lists could not be read.
				console.error(error);
				return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.fetch-failed`)));
			}),
			mergeMap(({ terminals, plans }) => {
				const terminalRoster = terminals.result ?? [];
				const terminalAliasById = new Map<string, string>(terminalRoster.filter((terminal) => terminal.id).map((terminal) => [terminal.id as string, terminal.alias]));
				const planAliasById = new Map<string, string>(plans.map((plan) => [plan.id, plan.alias]));

				const problem = this.findExportProblem(configurations, terminalRoster, plans, terminalAliasById, planAliasById);
				if (problem) {
					return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.${problem.key}`, { names: problem.names.join(', ') })));
				}

				const filePlans = configurations.map((configuration) => this.toFilePlan(configuration, terminalAliasById, planAliasById));
				const validationErrors: ImportErrorTypes[] = [];
				// Run the same validation as import before downloading, so exporting cannot bypass the form rules.
				this.buildFilePlans(
					filePlans.map((data) => ({ data, location: data.alias })),
					validationErrors,
				);
				if (validationErrors.length > 0) {
					const messages = validationErrors.flatMap((error) => (error.status === ImportStatus.ValidationError ? Object.values(error.errors).flatMap((fields) => Object.values(fields).map((message) => `${error.location}: ${message}`)) : [error.message]));
					return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.invalid-configurations`, { errors: messages.join('\n') })));
				}

				// The file is written synchronously, so defer() keeps a write failure inside the pipe for catchError.
				return defer(() => this.jsonFileTransfer.exportData(fileName, filePlans, ProductionPlanSchema)).pipe(
					map(() => configurations),
					catchError((error) => {
						console.error(error);
						throw new Error(this.translator.instant(`${this.translationPrefix}.export.errors.export-failed`, { error: error?.message }));
					}),
				);
			}),
		);
	}

	/** The first thing that would make a name in the file point at the wrong row or none, or make the file impossible to import - nothing is written then. */
	private findExportProblem(configurations: IConfiguration[], terminalRoster: ITerminal[], plans: IConfiguration[], terminalAliasById: Map<string, string>, planAliasById: Map<string, string>): { key: string; names: string[] } | undefined {
		const statusColumns = configurations.flatMap((configuration) => (configuration.statusColumns ?? []).map((column) => ({ configuration, column })));
		const label = ({ configuration, column }: (typeof statusColumns)[number]) => `${configuration.alias} / ${column.title}`;

		// A plan is told apart by its alias, in the file and in the environment it is imported into, so no alias may be carried by two plans - also not by one that is not exported, which could be linked to or updated by mistake.
		const duplicatePlans = [...new Set([...this.duplicatesOf(plans.map((plan) => plan.alias)), ...this.duplicatesOf(configurations.map((configuration) => configuration.alias))])];
		if (duplicatePlans.length > 0) {
			return { key: 'duplicate-aliases', names: duplicatePlans };
		}

		const referencedTerminalAliases = new Set(statusColumns.flatMap(({ column }) => (column.terminalIds ?? []).map((id) => terminalAliasById.get(id)).filter(Boolean)));
		const ambiguousTerminals = this.duplicatesOf(terminalRoster.map((terminal) => terminal.alias)).filter((alias) => referencedTerminalAliases.has(alias));
		if (ambiguousTerminals.length > 0) {
			return { key: 'ambiguous-terminals', names: ambiguousTerminals };
		}

		const missingTerminals = statusColumns.filter(({ column }) => (column.terminalIds ?? []).some((id) => !terminalAliasById.has(id))).map(label);
		if (missingTerminals.length > 0) {
			return { key: 'missing-terminals', names: missingTerminals };
		}

		const missingPlanLinks = statusColumns.filter(({ column }) => (column.planLinksIds ?? []).some((id) => !planAliasById.has(id))).map(label);
		if (missingPlanLinks.length > 0) {
			return { key: 'missing-plan-links', names: missingPlanLinks };
		}

		return undefined;
	}

	// A plan's columns keep the order it lists them in, which is the order they are shown in.
	private toFilePlan(configuration: IConfiguration, terminalAliasById: Map<string, string>, planAliasById: Map<string, string>): IProductionPlanFile {
		return {
			alias: configuration.alias,
			description: configuration.description ?? '',
			orderPositionSql: configuration.orderPositionSql ?? '',
			infoColumns: (configuration.infoColumns ?? []).map((column) => ({
				title: column.title,
				source: column.source ?? '',
				type: column.type,
				format: column.format ?? '',
			})),
			statusColumns: (configuration.statusColumns ?? []).map((column) => ({
				title: column.title,
				terminals: (column.terminalIds ?? []).map((id) => terminalAliasById.get(id) as string),
				planLinks: (column.planLinksIds ?? []).map((id) => planAliasById.get(id) as string),
			})),
		};
	}

	public importData(file: File | File[]): Observable<ImportResponse> {
		const files = Array.isArray(file) ? file : [file];
		const errors: ImportErrorTypes[] = [];

		// One file only: the upload dialog offers one, but its drop zone passes on every file dropped, and a second file would be checked against an environment the first is still changing.
		if (files.length !== 1) {
			this.pushParseError(errors, 'one-file-only', { count: files.length });
			return of({ successfulImports: [], errors });
		}

		return readFiles(files, 'binary').pipe(mergeMap((buffer) => this.readAndSave(buffer, errors).pipe(map((successfulImports) => ({ successfulImports, errors }) as ImportResponse))));
	}

	// Everything the file and the environment can be checked for happens before the first plan is saved, so a mistake anywhere
	// leaves the environment untouched instead of half-imported - only a save the backend itself refuses can still fail on its own, and a plan whose links cannot be set is then left created without them, reported as such.
	// Failures are listed in the order they happen: those of creating the new plans first, then those of setting the links.
	private readAndSave(buffer: ArrayBuffer, errors: ImportErrorTypes[]): Observable<SuccessFullImport[]> {
		// Validate the bytes, not the decoded text: a literal replacement character can be valid SQL or a column title.
		try {
			new TextDecoder('utf-8', { fatal: true }).decode(buffer);
		} catch {
			this.pushParseError(errors, 'invalid-encoding');
			return of([]);
		}

		const parseErrors: jsonImportError[] = [];
		let imported: Array<IImportedData<IProductionPlanFile>>;
		try {
			imported = this.jsonFileTransfer.importData(buffer, ProductionPlanSchema, parseErrors);
		} catch (error) {
			errors.push({ status: ImportStatus.ParsingError, message: error.message } as FailedParse);
			return of([]);
		}

		parseErrors.forEach((error) => errors.push({ status: ImportStatus.ParsingError, message: this.describeParseError(error) } as FailedParse));
		if (errors.length > 0) {
			return of([]);
		}

		const plans = this.buildFilePlans(imported, errors);
		if (errors.length > 0) {
			return of([]);
		}

		return forkJoin({
			terminals: this.terminalService.getTerminals({ includeArriveLeaveTerminals: true }),
			existing: this.configurationService.get(),
		}).pipe(
			map((rosters) => ({ rosters })),
			catchError((error) => {
				console.error(error);
				this.pushParseError(errors, 'fetch-failed');
				return of(undefined);
			}),
			mergeMap((fetched) => (fetched ? this.resolveAndSave(plans, fetched.rosters.terminals.result ?? [], fetched.rosters.existing, errors) : of([]))),
		);
	}

	private buildFilePlans(imported: Array<IImportedData<IProductionPlanFile>>, errors: ImportErrorTypes[]): IFilePlan[] {
		const plans = new Map<string, IFilePlan>();
		const duplicateAliases = new Set<string>();

		for (const { data, location } of imported) {
			if (!data) {
				this.pushParseError(errors, 'empty-plan', { location });
				continue;
			}
			if (plans.has(data.alias)) {
				duplicateAliases.add(data.alias);
				continue;
			}
			this.validateFields({ alias: data.alias, description: data.description, orderPositionSql: data.orderPositionSql, infoColumns: data.infoColumns ?? [], statusColumns: data.statusColumns ?? [] }, data.alias, location, errors);
			plans.set(data.alias, this.buildFilePlan(data, location, errors));
		}

		if (duplicateAliases.size > 0) {
			this.pushParseError(errors, 'duplicate-aliases-in-file', { names: [...duplicateAliases].join(', ') });
			return [];
		}

		return [...plans.values()];
	}

	private buildFilePlan(data: IProductionPlanFile, location: string, errors: ImportErrorTypes[]): IFilePlan {
		const plan: IFilePlan = { alias: data.alias, description: data.description ?? '', orderPositionSql: data.orderPositionSql ?? '', location, infoColumns: [], statusColumns: [] };

		for (const column of data.infoColumns ?? []) {
			// The transfer lets a null through where it expects an object, so an empty entry in the list reaches here.
			if (!column) {
				this.pushParseError(errors, 'empty-column', { alias: data.alias, list: 'infoColumns' });
				continue;
			}
			const columnLocation = this.locationOf(location, data.alias, 'info-column', column.title);
			this.validateFields({ title: column.title, source: column.source, format: column.format }, data.alias, columnLocation, errors);
			const type = Object.values(DataTypes).find((value) => value.toLowerCase() === (column.type ?? '').trim().toLowerCase());
			if (!type) {
				this.pushRowError(errors, data.alias, columnLocation, 'type', 'invalid-type', { value: column.type });
				continue;
			}
			plan.infoColumns.push({ title: column.title, source: column.source ?? '', type, format: column.format ?? '' });
		}

		for (const column of data.statusColumns ?? []) {
			if (!column) {
				this.pushParseError(errors, 'empty-column', { alias: data.alias, list: 'statusColumns' });
				continue;
			}
			const columnLocation = this.locationOf(location, data.alias, 'status-column', column.title);
			this.validateFields({ title: column.title }, data.alias, columnLocation, errors);
			plan.statusColumns.push({
				title: column.title,
				terminals: this.linksOf(column.terminals, data.alias, columnLocation, 'terminals', errors),
				planLinks: this.linksOf(column.planLinks, data.alias, columnLocation, 'planLinks', errors),
			});
		}

		return plan;
	}

	private validateFields(values: Partial<Record<keyof typeof productionPlanValidators, unknown>>, alias: string, location: string, errors: ImportErrorTypes[]): void {
		for (const field of Object.keys(values) as Array<keyof typeof productionPlanValidators>) {
			const validation = validateProductionPlanField(field, values[field]);
			if (validation) {
				const key = field === 'title' && validation['required'] ? 'missing-title' : field === 'infoColumns' ? 'missing-info-columns' : field === 'statusColumns' ? 'missing-status-columns' : field === 'orderPositionSql' ? 'invalid-sql' : `invalid-${field}`;
				this.pushRowError(errors, alias, location, field, key, { alias });
			}
		}
	}

	/** The aliases a status column lists as links. The same alias twice is refused, as it would put one id on the column twice. */
	private linksOf(aliases: string[] | undefined, id: string, location: string, property: string, errors: ImportErrorTypes[]): IFileLink[] {
		const links: IFileLink[] = [];
		const seen = new Set<string>();

		for (const entry of aliases ?? []) {
			// The transfer lets a null through where it expects a string: it is reported as the blank alias it is, which no terminal or plan has.
			const alias = entry ?? '';
			if (seen.has(alias)) {
				this.pushRowError(errors, id, location, property, 'duplicate-link-row', { alias });
			} else {
				seen.add(alias);
				links.push({ alias, location });
			}
		}

		return links;
	}

	/** Where a column sits in the file: the plan's own position and alias - the error list shows only this, not the plan it belongs to - then the column by its title. */
	private locationOf(planLocation: string, alias: string, kind: 'info-column' | 'status-column', title: string): string {
		return this.translator.instant(`${this.translationPrefix}.import.location.${kind}`, { location: planLocation, alias, title });
	}

	private resolveAndSave(plans: IFilePlan[], terminals: ITerminal[], existing: IConfiguration[], errors: ImportErrorTypes[]): Observable<SuccessFullImport[]> {
		const duplicateExisting = this.duplicatesOf(existing.map((plan) => plan.alias));
		if (duplicateExisting.length > 0) {
			this.pushParseError(errors, 'duplicate-aliases-in-roster', { names: duplicateExisting.join(', ') });
			return of([]);
		}

		const terminalIdsByAlias = new Map<string, string[]>();
		terminals.filter((terminal) => terminal.id).forEach((terminal) => terminalIdsByAlias.set(terminal.alias, [...(terminalIdsByAlias.get(terminal.alias) ?? []), terminal.id as string]));
		const existingByAlias = new Map(existing.map((plan) => [plan.alias, plan]));
		const aliasesInFile = new Set(plans.map((plan) => plan.alias));

		for (const plan of plans) {
			for (const column of plan.statusColumns) {
				for (const terminal of column.terminals) {
					const matches = terminalIdsByAlias.get(terminal.alias) ?? [];
					if (matches.length !== 1) {
						this.pushRowError(errors, plan.alias, terminal.location, 'terminals', matches.length === 0 ? 'unknown-terminal' : 'ambiguous-terminal', { alias: terminal.alias });
					}
				}
				for (const link of column.planLinks) {
					if (!aliasesInFile.has(link.alias) && !existingByAlias.has(link.alias)) {
						this.pushRowError(errors, plan.alias, link.location, 'planLinks', 'unknown-plan-link', { alias: link.alias });
					}
				}
			}
		}

		if (errors.length > 0) {
			return of([]);
		}

		// A plan's links hold the ids of the plans it links to, and a plan has no id before it exists - so the plans are saved in two rounds rather than in an order: first every new plan is created with no links at all, which
		// gives every plan an id, then every plan is saved with its links. A plan linking to another plan then makes no difference to which is saved first, so plans may link to each other in a circle.
		const idByAlias = new Map<string, string>(existing.map((plan) => [plan.alias, plan.id]));
		const notCreated = new Set<string>();

		return from(plans.filter((plan) => !existingByAlias.has(plan.alias))).pipe(
			concatMap((plan) => this.createWithoutLinks(plan, idByAlias, terminalIdsByAlias, notCreated, errors)),
			toArray(),
			concatMap(() => from(plans)),
			concatMap((plan) => this.saveWithLinks(plan, existingByAlias.get(plan.alias), idByAlias, terminalIdsByAlias, notCreated, errors)),
			toArray(),
		);
	}

	/** The plan as the backend takes it: its links are the ids of the plans they name, or none at all when `withLinks` is false. */
	private toConfiguration(plan: IFilePlan, id: string | undefined, idByAlias: Map<string, string>, terminalIdsByAlias: Map<string, string[]>, withLinks: boolean): IConfiguration {
		return {
			id,
			alias: plan.alias,
			description: plan.description,
			orderPositionSql: plan.orderPositionSql,
			infoColumns: plan.infoColumns,
			statusColumns: plan.statusColumns.map((column) => ({
				title: column.title,
				terminalIds: column.terminals.map((terminal) => (terminalIdsByAlias.get(terminal.alias) as string[])[0]),
				planLinksIds: withLinks ? column.planLinks.map((link) => idByAlias.get(link.alias)) : [],
			})),
		} as unknown as IConfiguration;
	}

	/** First round: a plan that does not exist yet is created without its links, so that it has an id for the plans that link to it. It emits nothing; a plan that cannot be created is reported on its own and the others go on. */
	private createWithoutLinks(plan: IFilePlan, idByAlias: Map<string, string>, terminalIdsByAlias: Map<string, string[]>, notCreated: Set<string>, errors: ImportErrorTypes[]): Observable<never> {
		const configuration = this.toConfiguration(plan, undefined, idByAlias, terminalIdsByAlias, false);

		// defer() keeps a request that fails before it is even sent inside the pipe, so it is reported for this plan and the others still go on.
		// The type of tap is given: ignoreElements accepts anything, which would otherwise pull the types of the pipe down to unknown.
		return defer(() => this.configurationService.create(configuration)).pipe(
			tap<IConfiguration>((created) => {
				if (created?.id) {
					idByAlias.set(plan.alias, created.id);
				}
			}),
			ignoreElements(),
			catchError((error) => {
				console.error(error);
				notCreated.add(plan.alias);
				this.pushFailure(errors, plan, this.saveFailedMessage(error, plan.alias));
				return EMPTY;
			}),
		);
	}

	/**
	 * Second round: the plan is saved with its links, now that every plan that could be created has an id. A new plan that links to nothing was complete after the first round. A new plan whose links cannot be set exists without them, and is reported as such: importing the file again finds it by its alias and sets
	 * them.
	 */
	private saveWithLinks(plan: IFilePlan, existing: IConfiguration | undefined, idByAlias: Map<string, string>, terminalIdsByAlias: Map<string, string[]>, notCreated: Set<string>, errors: ImportErrorTypes[]): Observable<SuccessFullImport> {
		if (notCreated.has(plan.alias)) {
			return EMPTY;
		}

		const isNew = !existing;
		const saved = { Id: plan.alias, status: isNew ? ImportStatus.SuccessCreate : ImportStatus.SuccessUpdate } as SuccessFullImport;
		if (isNew && plan.statusColumns.every((column) => column.planLinks.length === 0)) {
			return of(saved);
		}

		// Only a plan that could not be created has no id: a link to it cannot be set. A link to the plan itself is not one of those - its own id is what is looked for below.
		const notCreatedLinks = [...new Set(plan.statusColumns.flatMap((column) => column.planLinks.map((link) => link.alias)).filter((alias) => alias !== plan.alias && !idByAlias.get(alias)))];
		if (notCreatedLinks.length > 0) {
			const key = isNew ? 'links-dependency-not-created' : 'dependency-not-saved';
			this.pushFailure(errors, plan, this.translator.instant(`${this.translationPrefix}.import.errors.${key}`, { alias: plan.alias, names: notCreatedLinks.join(', ') }));
			return EMPTY;
		}

		const id = idByAlias.get(plan.alias);
		if (!id) {
			// It was created, but the answer carried no id to update it by.
			this.pushFailure(errors, plan, this.translator.instant(`${this.translationPrefix}.import.errors.links-not-saved`, { alias: plan.alias }));
			return EMPTY;
		}

		const configuration = this.toConfiguration(plan, id, idByAlias, terminalIdsByAlias, true);

		return defer(() => this.configurationService.update(configuration)).pipe(
			// An update that completes without answering is still an update that went through: the plan must not vanish from both lists.
			defaultIfEmpty(undefined),
			map(() => saved),
			catchError((error) => {
				console.error(error);
				this.pushFailure(errors, plan, isNew ? this.translator.instant(`${this.translationPrefix}.import.errors.links-not-saved`, { alias: plan.alias }) : this.saveFailedMessage(error, plan.alias));
				return EMPTY;
			}),
		);
	}

	private pushFailure(errors: ImportErrorTypes[], plan: IFilePlan, message: string): void {
		errors.push({ Id: plan.alias, location: plan.location, status: ImportStatus.Failed, message } as FailedImport);
	}

	private saveFailedMessage(error: unknown, alias: string): string {
		const status = (error as HttpErrorResponse)?.status;
		const suffix = status === 409 ? '-alias-conflict' : status === 400 ? '-bad-request' : '';
		return this.translator.instant(`${this.translationPrefix}.import.errors.save-failed${suffix}`, { alias });
	}

	/**
	 * The transfer addresses a problem as `[row 1].statusColumns[0].terminals[1]` - an English 'row' and indexes counted from 0. This words it as the rest of the import does: the position of the plan the way the plan's own errors name it ('Item 1'), then the property with its entries counted from 1 ('statusColumns 1 ›
	 * terminals 2').
	 */
	private describeParseError({ path, error }: jsonImportError): string {
		const match = /^\[row (\d+)\](.*)$/.exec(path);
		if (!match) {
			return `${path}: ${error}`;
		}

		const position = this.translator.instant('transferService.json.row-location', { index: Number(match[1]) });
		const property = match[2]
			.replace(/^\./, '')
			.replace(/\[(\d+)\]/g, (_, index: string) => ` ${Number(index) + 1}`)
			.replaceAll('.', ' › ');

		return `${property ? `${position}, ${property}` : position}: ${error}`;
	}

	private pushParseError(errors: ImportErrorTypes[], key: string, params?: object): void {
		errors.push({ status: ImportStatus.ParsingError, message: this.translator.instant(`${this.translationPrefix}.import.errors.${key}`, params) } as FailedParse);
	}

	private pushRowError(errors: ImportErrorTypes[], id: string, location: string, property: string, key: string, params: object): void {
		errors.push({ Id: id, location, status: ImportStatus.ValidationError, errors: { [property]: { [key]: this.translator.instant(`${this.translationPrefix}.import.errors.${key}`, params) } } } as FailedValidationImport);
	}

	/** The values that occur 2+ times, each once. */
	private duplicatesOf(values: string[]): string[] {
		const seen = new Set<string>();
		const duplicates = new Set<string>();
		values.forEach((value) => (seen.has(value) ? duplicates : seen).add(value));
		return [...duplicates];
	}
}
